// src/store.js
import { createStore } from "vuex";
import dbManager from "@/db";
import { TENSES, TENSES_KEYS, PRONOUNS, PRONOUNS_KEYS } from "@/constants";

const store = createStore({
  state: {
    topScores: [],
    currentScore: 0,
    username: "anonymous",
    isLoading: false,
    selectedTenses: [...TENSES_KEYS].slice(0, 3),
    selectedPronouns: [...PRONOUNS_KEYS].slice(0, 2),
    difficulty: "easy",
  },
  mutations: {
    setTopScores(state, scores) {
      state.topScores = scores;
    },
    setCurrentScore(state, score) {
      state.currentScore = score;
    },
    setUsername(state, username) {
      state.username = username;
    },
    setLoading(state, isLoading) {
      state.isLoading = isLoading; // Set loading state
    },
    setSelectedTenses(state, selectedTenses) {
      state.selectedTenses = selectedTenses;
    },
    toggleTense(state, tenseKey) {
      if (state.selectedTenses.includes(tenseKey)) {
        state.selectedTenses = state.selectedTenses.filter(
          (tense) => tense !== tenseKey
        );
        if (state.selectedTenses.length === 0) {
          state.selectedTenses.push(TENSES_KEYS[0]); // Ensure at least one tense is always selected
        }
      } else {
        state.selectedTenses.push(tenseKey);
      }
    },
    setSelectedPronouns(state, selectedPronouns) {
      state.selectedPronouns = selectedPronouns;
    },
    togglePronoun(state, pronounKey) {
      if (state.selectedPronouns.includes(pronounKey)) {
        state.selectedPronouns = state.selectedPronouns.filter(
          (pronoun) => pronoun !== pronounKey
        );
        if (state.selectedPronouns.length === 0) {
          state.selectedPronouns.push(PRONOUNS_KEYS[0]); // Ensure at least one pronoun is always selected
        }
      } else {
        state.selectedPronouns.push(pronounKey);
      }
    },
    setDifficulty(state, difficulty) {
      state.difficulty = difficulty;
    },
  },
  actions: {
    async fetchTopScores({ commit }) {
      commit("setLoading", true); // Set loading to true
      try {
        const scores = await dbManager.getTopScores(10); // Fetch top 5 scores
        commit("setTopScores", scores);
      } catch (error) {
        console.error("Error fetching top scores:", error);
      } finally {
        commit("setLoading", false); // Set loading to false
      }
    },
    async saveScore({ state }) {
      try {
        if (state.currentScore)
          await dbManager.saveScore(state.username, state.currentScore); // Save score with username
        this.dispatch("fetchTopScores"); // Refresh top scores after saving
      } catch (error) {
        console.error("Error saving score:", error);
      }
    },
    updateUsername({ commit }, username) {
      commit("setUsername", username);
    },
    updateScore({ commit }, score) {
      commit("setCurrentScore", score);
    },
    toggleTense({ commit }, tenseKey) {
      commit("toggleTense", tenseKey);
    },
    togglePronoun({ commit }, pronounKey) {
      commit("togglePronoun", pronounKey);
    },
    setDifficulty({ commit }, difficulty) {
      commit("setDifficulty", difficulty);

      let selectedTenses = [];
      let selectedPronouns = [];

      // Based on difficulty, update selected tenses and pronouns
      if (difficulty === "easy") {
        selectedTenses = TENSES_KEYS.slice(0, 3);
        selectedPronouns = PRONOUNS_KEYS.slice(0, 2);
      } else if (difficulty === "medium") {
        selectedTenses = TENSES_KEYS.slice(0, 3);
        selectedPronouns = PRONOUNS_KEYS.slice(0, 3);
      } else if (difficulty === "hard") {
        selectedTenses = TENSES_KEYS.slice(0, 4);
        selectedPronouns = PRONOUNS_KEYS.slice(0, 4);
      } else if (difficulty === "expert") {
        selectedTenses = TENSES_KEYS;
        selectedPronouns = PRONOUNS_KEYS;
      }

      commit("setSelectedTenses", selectedTenses);
      commit("setSelectedPronouns", selectedPronouns);
    },
  },
  getters: {
    topScores: (state) => state.topScores,
    leaderboardMinScore: (state) => {
      if (state.topScores.length > 0) {
        return state.topScores[state.topScores.length - 1].score;
      }
      return 0; // Return 0 if there are no scores
    },
    currentScore: (state) => state.currentScore,
    username: (state) => state.username,
    tenses: (state) => TENSES,
    tensesKeys: (state) => TENSES_KEYS,
    selectedTenses: (state) => state.selectedTenses,
    pronouns: (state) => PRONOUNS,
    pronounsKeys: (state) => PRONOUNS_KEYS,
    selectedPronouns: (state) => state.selectedPronouns,
    difficulty: (state) => state.difficulty,
  },
});

export default store;
